import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Grid,
  Paper,
  Checkbox,
  FormControlLabel,
  FormGroup,
  InputAdornment,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { ArrowBack, Save, Cancel, Search } from '@mui/icons-material';
import { apiService } from '../../services/apiService';
import SettingsSidebar from '../../components/Common/SettingsSidebar';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

interface Permission {
  id: number;
  name: string;
  display_name: string;
  module: string;
}

const AddRole: React.FC = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [permissions, setPermissions] = useState<Permission[]>([]);
  const [permissionModules, setPermissionModules] = useState<string[]>([]);
  const [permissionSearchTerm, setPermissionSearchTerm] = useState('');
  
  const [formData, setFormData] = useState({
    name: '',
    display_name: '',
    description: '',
    permissions: [] as number[],
  });

  const [errors, setErrors] = useState<Record<string, string>>({});

  useEffect(() => {
    fetchPermissions();
  }, []);

  const fetchPermissions = async () => {
    try {
      const response = await apiService.getPermissions();
      setPermissions(response.data.data || response.data);
      
      // Get unique modules
      const modules: string[] = Array.from(new Set((response.data.data || response.data).map((p: Permission) => p.module)));
      setPermissionModules(modules);
    } catch (error: any) {
      console.error('Error fetching permissions:', error);
      toast.error(error.message || 'Failed to fetch permissions');
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    
    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const handleSelectChange = (e: any) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    
    // Clear error when user selects
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const handlePermissionChange = (permissionId: number, checked: boolean) => {
    setFormData(prev => ({
      ...prev,
      permissions: checked 
        ? [...prev.permissions, permissionId]
        : prev.permissions.filter(id => id !== permissionId)
    }));
  };

  const validateForm = () => {
    const newErrors: Record<string, string> = {};

    if (!formData.name.trim()) {
      newErrors.name = 'Role name is required';
    } else if (!/^[a-z_]+$/.test(formData.name)) {
      newErrors.name = 'Role name must contain only lowercase letters and underscores';
    }

    if (!formData.display_name.trim()) {
      newErrors.display_name = 'Display name is required';
    }

    if (!formData.description.trim()) {
      newErrors.description = 'Description is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    setLoading(true);
    try {
      const roleData = {
        name: formData.name,
        display_name: formData.display_name,
        description: formData.description,
        permissions: formData.permissions,
      };

      await apiService.createRole(roleData);
      toast.success('Role created successfully');
      
      // Redirect to roles list after successful creation
      setTimeout(() => {
        navigate('/roles');
      }, 1500);
    } catch (error: any) {
      console.error('Error creating role:', error);
      
      // Handle validation errors from the API
      if (error.response && error.response.data && error.response.data.errors) {
        const apiErrors = error.response.data.errors;
        const newErrors: Record<string, string> = {};
        
        // Map API errors to form errors
        if (apiErrors.name) {
          newErrors.name = apiErrors.name[0];
        }
        if (apiErrors.display_name) {
          newErrors.display_name = apiErrors.display_name[0];
        }
        if (apiErrors.description) {
          newErrors.description = apiErrors.description[0];
        }
        if (apiErrors.permissions) {
          newErrors.permissions = apiErrors.permissions[0];
        }
        
        setErrors(newErrors);
      } else {
        toast.error(error.message || 'Failed to create role');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    navigate('/roles');
  };

  return (
    <Box sx={{ backgroundColor: '#f0f9ff', minHeight: '100vh', p: 3 }}>
      <ToastContainer />
      
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4" sx={{ fontWeight: 600, fontSize: '2rem' }}>
          Settings
        </Typography>
        <Button
          variant="outlined"
          startIcon={<ArrowBack />}
          onClick={handleCancel}
          sx={{
            borderRadius: '8px',
            borderColor: '#d1d5db',
            color: '#374151',
            '&:hover': {
              borderColor: '#9ca3af',
              backgroundColor: '#f9fafb',
            },
          }}
        >
          Back to Roles
        </Button>
      </Box>

      <Grid container spacing={3}>
        {/* Left Sidebar */}
        <Grid size={{ xs: 12, md: 3 }}>
          <SettingsSidebar />
        </Grid>

        {/* Main Content */}
        <Grid size={{ xs: 12, md: 9 }}>
          <Card sx={{ height: 'calc(100vh - 140px)', borderRadius: '12px' }}>
            <CardContent sx={{ height: '100%', display: 'flex', flexDirection: 'column', p: 0 }}>
              {/* Form Header */}
              <Box sx={{ 
                p: 3, 
                borderBottom: '1px solid #e5e7eb',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center'
              }}>
                <Typography variant="h5" sx={{ fontWeight: 600, color: '#374151' }}>
                  Create New Role
                </Typography>
              </Box>

              {/* Form Content */}
              <Box sx={{ flex: 1, overflow: 'auto', p: 3 }}>
                <form onSubmit={handleSubmit}>
                  <Grid container spacing={3}>
                    {/* Basic Information */}
                    <Grid size={{ xs: 12 }}>
                      <Paper sx={{ p: 3, backgroundColor: '#f9fafb' }}>
                        <Typography variant="h6" sx={{ fontWeight: 600, color: '#374151', mb: 2 }}>
                          Basic Information
                        </Typography>
                        <Grid container spacing={3}>
                          <Grid size={{ xs: 12, md: 6 }}>
                            <TextField
                              fullWidth
                              name="name"
                              label="Role Name"
                              value={formData.name}
                              onChange={handleInputChange}
                              error={!!errors.name}
                              helperText={errors.name || 'Use lowercase letters and underscores only (e.g., user_manager)'}
                              sx={{
                                '& .MuiOutlinedInput-root': {
                                  borderRadius: '8px',
                                },
                              }}
                            />
                          </Grid>
                          <Grid size={{ xs: 12, md: 6 }}>
                            <TextField
                              fullWidth
                              name="display_name"
                              label="Display Name"
                              value={formData.display_name}
                              onChange={handleInputChange}
                              error={!!errors.display_name}
                              helperText={errors.display_name}
                              sx={{
                                '& .MuiOutlinedInput-root': {
                                  borderRadius: '8px',
                                },
                              }}
                            />
                          </Grid>
                          <Grid size={{ xs: 12 }}>
                            <TextField
                              fullWidth
                              name="description"
                              label="Description"
                              value={formData.description}
                              onChange={handleInputChange}
                              error={!!errors.description}
                              helperText={errors.description}
                              multiline
                              rows={3}
                              sx={{
                                '& .MuiOutlinedInput-root': {
                                  borderRadius: '8px',
                                },
                              }}
                            />
                          </Grid>
                        </Grid>
                      </Paper>
                    </Grid>

                    {/* Permissions */}
                    <Grid size={{ xs: 12 }}>
                      <Paper sx={{ p: 3, backgroundColor: '#f9fafb', borderRadius: '12px' }}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                          <Typography variant="h6" sx={{ fontWeight: 600, color: '#374151' }}>
                            Permissions
                          </Typography>
                          <Typography variant="body2" sx={{ color: '#6b7280' }}>
                            {formData.permissions.length} selected (Total: {permissions.length})
                          </Typography>
                        </Box>
                        
                        {/* Permission Search */}
                        <TextField
                          fullWidth
                          placeholder="Search permissions..."
                          size="small"
                          value={permissionSearchTerm}
                          onChange={(e) => setPermissionSearchTerm(e.target.value)}
                          InputProps={{
                            startAdornment: (
                              <InputAdornment position="start">
                                <Search sx={{ color: '#9ca3af', fontSize: '1.2rem' }} />
                              </InputAdornment>
                            ),
                          }}
                          sx={{
                            mb: 3,
                            '& .MuiOutlinedInput-root': {
                              borderRadius: '8px',
                              backgroundColor: 'white',
                              '&:hover': {
                                backgroundColor: 'white',
                              },
                              '&.Mui-focused': {
                                backgroundColor: 'white',
                              },
                            },
                          }}
                        />
                        
                        <Grid container spacing={3}>
                          {permissionModules.map((module) => (
                            <Grid size={{ xs: 12, md: 6 }} key={module}>
                              <Card sx={{ 
                                borderRadius: '8px', 
                                border: '1px solid #e5e7eb',
                                backgroundColor: 'white',
                                '&:hover': {
                                  borderColor: '#d1d5db',
                                }
                              }}>
                                <CardContent sx={{ p: 2 }}>
                                  <Typography variant="subtitle1" sx={{ 
                                    fontWeight: 600, 
                                    mb: 2, 
                                    color: '#374151',
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 1
                                  }}>
                                    <Box sx={{ 
                                      width: 8, 
                                      height: 8, 
                                      borderRadius: '50%', 
                                      backgroundColor: '#4A90E2' 
                                    }} />
                                    {module.charAt(0).toUpperCase() + module.slice(1)} Module
                                  </Typography>
                                  <FormGroup>
                                    {permissions
                                      .filter(permission => permission.module === module)
                                      .filter(permission => 
                                        permission.display_name.toLowerCase().includes(permissionSearchTerm.toLowerCase()) ||
                                        permission.name.toLowerCase().includes(permissionSearchTerm.toLowerCase())
                                      )
                                      .map((permission) => (
                                        <FormControlLabel
                                          key={permission.id}
                                          control={
                                            <Checkbox
                                              checked={formData.permissions.includes(permission.id)}
                                              onChange={(e) => handlePermissionChange(permission.id, e.target.checked)}
                                              sx={{
                                                '&.Mui-checked': {
                                                  color: '#4A90E2',
                                                },
                                                '&:hover': {
                                                  backgroundColor: 'rgba(74, 144, 226, 0.04)',
                                                },
                                              }}
                                            />
                                          }
                                          label={
                                            <Typography variant="body2" sx={{ 
                                              fontWeight: 500,
                                              color: '#374151',
                                              lineHeight: 1.4,
                                              ml: 1
                                            }}>
                                              {permission.display_name}
                                            </Typography>
                                          }
                                          sx={{
                                            mb: 0.5,
                                            '&:hover': {
                                              backgroundColor: 'rgba(74, 144, 226, 0.02)',
                                              borderRadius: '4px',
                                            },
                                          }}
                                        />
                                      ))}
                                  </FormGroup>
                                </CardContent>
                              </Card>
                            </Grid>
                          ))}
                        </Grid>
                      </Paper>
                    </Grid>
                  </Grid>
                </form>
              </Box>

              {/* Form Footer */}
              <Box sx={{ 
                mt: 4, 
                pt: 3, 
                borderTop: '1px solid #e5e7eb',
                backgroundColor: '#f8fafc',
                borderRadius: '0 0 12px 12px',
                margin: '0 -24px -24px -24px',
                padding: '24px'
              }}>
                <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2 }}>
                  <Button
                    type="submit"
                    variant="contained"
                    disabled={loading}
                    onClick={handleSubmit}
                    sx={{
                      px: 4,
                      py: 2,
                      borderRadius: '8px',
                      backgroundColor: '#4A90E2',
                      color: 'white',
                      fontWeight: 600,
                      fontSize: '1rem',
                      textTransform: 'none',
                      minWidth: '140px',
                      '&:hover': {
                        backgroundColor: '#3b82f6',
                        boxShadow: '0 4px 12px rgba(74, 144, 226, 0.3)',
                      },
                      '&:disabled': {
                        backgroundColor: '#9ca3af',
                        color: 'white',
                      },
                    }}
                  >
                    {loading ? 'Creating...' : 'Create Role'}
                  </Button>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
};

export default AddRole;
